import type { GenerationFailureDto } from '@/types/api-generated'
import { describeGenerationFailure, isFixedInTemplateField, stillDescribesChosenTemplate } from '@/utils/generationFailure'
import { GenerationFailurePanel } from '@/components/webs/landingPage/GenerationFailurePanel'
import { useState, useEffect, useLayoutEffect, useCallback, useRef, useMemo } from 'preact/hooks'
import { useAuth } from '@/auth/useAuth'
import { useEmailAssetChat } from '@/hooks/useEmailAssetChat'
import { useVariableCatalog } from '@/hooks/useVariableCatalog'
import { toUnlayerMergeTags, TEMPLATE_SLOT_VARIABLES } from '@/components/assets/emailMergeTags'
import { UnlayerDesigner, type UnlayerDesignerHandle } from '@/components/assets/UnlayerDesigner'
import { EmailCopyEditorPanel, type EmailCopyEditorPanelHandle } from '@/components/assets/EmailCopyEditorPanel'
import { RocketLaunch } from '@/components/webs/landingPage/RocketLaunch'
import { WebsterChatPanel } from '@/components/webster/WebsterChatPanel'
import { SaveStatusIndicator, type SaveStatus } from '@/components/common/SaveStatusIndicator'
import { EmptyDesignRecovery } from '@/components/assets/EmptyDesignRecovery'
import { assetApi, type AssetVersionViewDto, type WebAssetViewDto } from '@/api/asset'
import { useToast } from '@/hooks/useToast'
import { hasEmailBody } from '@/utils/unlayerDesign'
import { getDisplayErrorMessages } from '@/utils/errorHandling'

/** Why Save as Version is held while the template canvas is still starting (ORB-960). */
export const EDITOR_LOADING_REASON = 'The email editor is still loading — save will be available once the email appears'
/** Why Save as Version is held on an email whose stored design is empty (ORB-960). */
export const EMPTY_DESIGN_REASON = "This email's design is empty — restore a version or regenerate it before saving"

type RocketPhase = 'idle' | 'blasting-off' | 'ready'

export interface EmailBuilderProps {
    clientCompanyId: string
    workspaceId: string
    campaignId: string
    webId: string
    assetId: string
    assetType: 'Email Template' | 'Email Copy'
    assetDetail: WebAssetViewDto | null
    refreshAsset: () => Promise<unknown>
    /** Parent-owned ref filled in by EmailBuilder with a flush callback. The Save-as-
     *  Version button calls it before promote to force Unlayer to compile/export its
     *  current state, so freshly Webster-generated assets with null DraftHtml get
     *  compiled before the server tries to snapshot them. */
    flushRef: { current: (() => Promise<boolean | void>) | null }
    /** Parent-owned ref EmailBuilder fills with a "compile the current editor HTML"
     *  callback. The top-bar Send Test button calls it to mail the live, unsaved editor
     *  HTML as an HtmlOverride so in-progress edits are testable. Returns '' before the
     *  editor mounts. */
    getHtmlRef: { current: (() => Promise<string>) | null }
    /** Parent-owned ref EmailBuilder fills with a refetch+reseed routine. The top-bar
     *  "Revert to last version" button calls it after a successful revert so the editor
     *  reloads off the restored draft (template canvas remounts; the copy panel reseeds
     *  off the refreshed asset prop). */
    reseedRef: { current: (() => Promise<void>) | null }
    /** Reports the full generating state (`generating || autoGenerating`) so the parent
     *  can disable Save-as-Version while Webster is working — covers the INITIAL
     *  auto-generation (autoGenerating, incl. the post-generation rocket animation), not
     *  just chat-driven rebuilds. Reporting only the chat flag let users save an
     *  un-generated draft mid-generation (ORB-314). */
    onGeneratingChange?: (value: boolean) => void
    /** The asset's saved versions, newest or not, for restoring a template whose design is empty (ORB-960). */
    versions?: AssetVersionViewDto[]
    /** Reports why Save as Version must wait (the template canvas is still loading, or the email's design is
     *  empty), or null once it can save (ORB-960). */
    onEditorBlockedChange?: (reason: string | null) => void
}

export function EmailBuilder({
    clientCompanyId,
    workspaceId,
    campaignId,
    webId,
    assetId,
    assetType,
    assetDetail,
    refreshAsset,
    flushRef,
    getHtmlRef,
    reseedRef,
    onGeneratingChange,
    versions = [],
    onEditorBlockedChange,
}: EmailBuilderProps) {
    const unlayerEditorRef = useRef<UnlayerDesignerHandle>(null)
    const emailCopyPanelRef = useRef<EmailCopyEditorPanelHandle>(null)
    const { showError } = useToast()
    // Whether the template canvas has loaded its design. Until it has, an export returns `{}` (ORB-960).
    const [templateEditorReady, setTemplateEditorReady] = useState(false)
    // The same for an Email Copy's editor, reported up through EmailCopyEditorPanel.
    const [copyEditorReady, setCopyEditorReady] = useState(false)

    // ORB-466: Unlayer's merge-tag menu is now driven by the web's variable catalog instead of
    // a hardcoded list, so it shows the tokens that actually resolve for THIS web, with this
    // web's real values. Templates additionally expose the slot markers, which are template
    // mechanics rather than web data and so aren't in the catalog.
    const { data: variableCatalog } = useVariableCatalog(workspaceId, webId)
    const templateMergeTags = useMemo(
        () => toUnlayerMergeTags(variableCatalog?.Groups ?? [], 'EmailBody', TEMPLATE_SLOT_VARIABLES),
        [variableCatalog],
    )

    const draftHtml = assetDetail?.DraftHtml
    const draftDesign = assetDetail?.DraftDesign
    const draftConversationSessionId = assetDetail?.DraftConversationSessionId

    // ── Template canvas auto-save ──
    // Persists BOTH Unlayer's design JSON (canonical editable source) and its exported
    // HTML (used for preview / send). Pattern mirrors EmailCopyEditorPanel: every change
    // marks dirty, a debounced timer fires the save, the last-persisted refs let the
    // post-save refetch land without re-mounting the editor or looping save requests.
    const [templateSaveStatus, setTemplateSaveStatus] = useState<SaveStatus>('idle')
    const [templateSaveError, setTemplateSaveError] = useState<string | null>(null)
    const pendingHtmlRef = useRef<string | null>(null)
    const pendingDesignRef = useRef<unknown>(null)
    const lastPersistedHtmlRef = useRef<string | null>(draftHtml ?? null)
    const lastPersistedDesignRef = useRef<unknown>(assetDetail?.DraftDesign ?? null)
    const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    /** Persists the pending design and HTML. Resolves false when the update failed; nothing to save counts as saved. */
    const runTemplateSave = useCallback(async (): Promise<boolean> => {
        if (!assetDetail) return false
        const htmlSnapshot = pendingHtmlRef.current
        const designSnapshot = pendingDesignRef.current
        const htmlChanged = htmlSnapshot != null && htmlSnapshot !== lastPersistedHtmlRef.current
        const designChanged = designSnapshot != null && designSnapshot !== lastPersistedDesignRef.current
        if (!htmlChanged && !designChanged) return true

        setTemplateSaveStatus('saving')
        setTemplateSaveError(null)
        const response = await assetApi.updateAsset(clientCompanyId, workspaceId, campaignId, assetDetail.Id, {
            Id: assetDetail.Id,
            CampaignId: assetDetail.CampaignId,
            ...(htmlChanged && htmlSnapshot != null ? { DraftHtml: htmlSnapshot } : {}),
            ...(designChanged && designSnapshot != null ? { DraftDesign: designSnapshot } : {}),
        })
        if (response.data) {
            if (htmlSnapshot != null) lastPersistedHtmlRef.current = htmlSnapshot
            if (designSnapshot != null) lastPersistedDesignRef.current = designSnapshot
            // If a fresh edit landed while in-flight, stay dirty so the next debounce
            // tick triggers another save instead of falsely showing "Saved".
            const stillMatches =
                pendingHtmlRef.current === htmlSnapshot &&
                pendingDesignRef.current === designSnapshot
            setTemplateSaveStatus(stillMatches ? 'saved' : 'dirty')
            await refreshAsset()
            return true
        }
        setTemplateSaveStatus('error')
        setTemplateSaveError(getDisplayErrorMessages(response.error, 'Save failed.').join(' '))
        return false
    }, [clientCompanyId, workspaceId, campaignId, assetDetail, refreshAsset])

    // Unlayer fires this on every design:updated event with both the design JSON and the
    // freshly-compiled HTML. We capture both into pending refs and schedule a debounced
    // save — same cadence pattern Email Copy uses.
    const handleUnlayerUpdate = useCallback((design: unknown, html: string) => {
        pendingDesignRef.current = design
        pendingHtmlRef.current = html
        const designChanged = design !== lastPersistedDesignRef.current
        const htmlChanged = html !== lastPersistedHtmlRef.current
        if (!designChanged && !htmlChanged) return
        setTemplateSaveStatus('dirty')

        if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
        saveTimerRef.current = setTimeout(() => { void runTemplateSave() }, 800)
    }, [runTemplateSave])

    const handleTemplateSaveRetry = useCallback(() => { void runTemplateSave() }, [runTemplateSave])

    // Force Unlayer to export its current design + HTML and persist whatever differs from
    // the last save. Called by Save as Version before promote, so a freshly Webster-generated
    // template with null DraftHtml gets compiled (Unlayer's `design:updated` only fires after
    // an edit settles; users can otherwise click Save before that lands).
    // ORB-960: an editor that never finished loading exports an empty design and no HTML, and saving those wiped a
    // template's real design. So nothing is exported or saved until the canvas is ready, and an export with no body
    // is refused; resolving false tells Save as Version not to promote.
    const flushTemplate = useCallback(async (): Promise<boolean> => {
        if (!unlayerEditorRef.current) return false
        if (!unlayerEditorRef.current.isReady()) {
            showError('Not saved yet', 'The email editor is still loading. Wait for the email to appear, then save.')
            return false
        }
        const design = await unlayerEditorRef.current.getDesign()
        if (!hasEmailBody(design)) {
            showError('Not saved', 'The editor returned an empty design, so nothing was saved. Reload the page and try again.')
            return false
        }
        const html = await unlayerEditorRef.current.getHtml()
        pendingDesignRef.current = design
        pendingHtmlRef.current = html
        if (saveTimerRef.current) {
            clearTimeout(saveTimerRef.current)
            saveTimerRef.current = null
        }
        // A failed update leaves the last saved draft in place, and promoting would snapshot that instead.
        return runTemplateSave()
    }, [runTemplateSave, showError])

    // Always register the flush callback for email assets. The flush snapshots Unlayer's
    // current design + HTML, cancels any pending debounce, and persists whatever differs
    // from the last save — covers the "freshly Webster-generated, null DraftHtml" case
    // AND the "user typed within the 800ms debounce window" case where the canvas is
    // dirtier than the last persisted state. Both flush implementations are no-ops when
    // nothing has changed, so there's no cost to always registering.
    useEffect(() => {
        flushRef.current = assetType === 'Email Template'
            ? flushTemplate
            : async () => (await emailCopyPanelRef.current?.flushAndSave()) ?? false
        return () => { flushRef.current = null }
    }, [assetType, flushTemplate, flushRef])

    // Register a "compile the current editor HTML" callback for the top-bar Send Test
    // button. Unlike flush this never persists — it just exports the live Unlayer HTML so
    // an in-progress edit is test-sent as-is. Both editor types expose getHtml (Email Copy
    // through its panel handle, Email Template directly off the designer ref).
    useEffect(() => {
        getHtmlRef.current = assetType === 'Email Template'
            ? async () => (await unlayerEditorRef.current?.getHtml()) ?? ''
            : async () => (await emailCopyPanelRef.current?.getHtml()) ?? ''
        return () => { getHtmlRef.current = null }
    }, [assetType, getHtmlRef])

    // Cleanup the pending debounce on unmount so a stale timer can't fire against a
    // disposed component.
    useEffect(() => () => {
        if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    }, [])

    // Auto-dismiss the "Saved" pill ~1.5s after it settles so it doesn't permanently
    // overlap the eye/preview button at the top-right of the canvas. Any new edit flips
    // status back to 'dirty' before this fires; the cleanup function tears down the
    // timer in that case, so the pill seamlessly transitions to 'Saving…' instead of
    // briefly hiding.
    useEffect(() => {
        if (templateSaveStatus !== 'saved') return
        const t = setTimeout(() => setTemplateSaveStatus('idle'), 1500)
        return () => clearTimeout(t)
    }, [templateSaveStatus])

    // Shell state is driven by the asset's persisted DraftGenerationStatus and by nothing else.
    // A missing status means different things for the two types, because only one of them starts
    // a generation when it is created.
    //
    // CreateWebAsset enqueues for an Email Template, which is created already Generating (ORB-1044).
    // A template with no status and no draft predates that: one whose separate status write failed
    // while the job it enqueued ran. The rocket is right, and — through onGeneratingChange — it is also what
    // holds the Save as Version gate shut while the draft is mid-write (ORB-314). Reading it as
    // idle would let a blank design be promoted in that window.
    //
    // An Email Copy enqueues nothing at creation. ORB-363's "Custom (blank — write it yourself)"
    // gave a null status a second and entirely legitimate meaning — an email the user intends to
    // write themselves — and the old fallback could not tell the two apart, because it asked only
    // about the absence of a draft. Nothing is queued for those, so no `rebuilt` and no `Failed`
    // can arrive, and every exit from this shell runs through one of those two. The builder sat on
    // the rocket forever with the chat input disabled, locking the user out of the prompt that was
    // the only way to start the generation it was pretending to run (ORB-876).
    //
    // Scoping the fallback by type also unsticks the copies already in that state: they hold a
    // null status and no draft, so they now open as an empty editor the user can type into. No
    // backfill, and nothing to migrate.
    //
    // A catalog Email Copy is not affected either way — it is bound to the web's template and
    // arrives with that design already cloned onto it, so it has a draft before the job is queued.
    const hasDraft = !!draftHtml || !!draftDesign
    const persistedGenStatus = assetDetail?.DraftGenerationStatus ?? null
    const isFailed = persistedGenStatus === 'Failed'
    const failure = assetDetail?.DraftGenerationFailure ?? assetDetail?.DraftGenerationError ?? null
    // A copy whose failure is fixed in its own Template field keeps the editor that holds that field, with the
    // failure as a banner above it while it still applies. The panel would replace the very field it tells the
    // person to use.
    const copyFailureView = isFailed && assetType !== 'Email Template' ? describeGenerationFailure(failure, 'email') : null
    const fixedInTemplateField = !!copyFailureView && isFixedInTemplateField(copyFailureView)
    const showsFailurePanel = isFailed && !fixedInTemplateField
    const templateFieldFailure = fixedInTemplateField
        && stillDescribesChosenTemplate(copyFailureView, assetDetail?.EmailTemplateAssetId ?? null)
    const isPersistedGenerating = persistedGenStatus === 'Generating'
    const shouldStartGenerating = isPersistedGenerating
        || (assetType === 'Email Template' && persistedGenStatus === null && !hasDraft)

    const [autoGenerating, setAutoGenerating] = useState(shouldStartGenerating)
    const [rocketPhase, setRocketPhase] = useState<RocketPhase>(shouldStartGenerating ? 'idle' : 'ready')

    // Bumped to remount the template canvas off a freshly restored draft after a revert.
    // Auto-save deliberately does NOT bump this (same content-compare reasoning as
    // EmailCopyEditorPanel) — remounting mid-edit would drop the user's place.
    const [templateDesignerKey, setTemplateDesignerKey] = useState(0)

    // ORB-960: a stored design with no body is what a half-loaded editor saved. A template shows the restore panel
    // instead of a blank canvas, a copy points at Reset to template, and neither can be saved as a version until
    // it holds a real design again. While the template canvas is starting, saving waits for it too.
    const designIsEmpty = draftDesign != null && !hasEmailBody(draftDesign)
    const canvasExpected = !showsFailurePanel && rocketPhase === 'ready' && !designIsEmpty
    // A copy with no template bound has no editor yet, and Save is already held with "Pick a template".
    const editorLoading = assetType === 'Email Template'
        ? canvasExpected && !templateEditorReady
        : canvasExpected && !!assetDetail?.EmailTemplateAssetId && !copyEditorReady
    const editorBlockedReason = designIsEmpty
        ? EMPTY_DESIGN_REASON
        : editorLoading ? EDITOR_LOADING_REASON : null
    useEffect(() => {
        onEditorBlockedChange?.(editorBlockedReason)
    }, [editorBlockedReason, onEditorBlockedChange])

    // The rocket blast-off splash plays once, for the INITIAL design only. This ref is the "are we
    // still waiting for that first design" flag — true until it lands, then false for every rebuild
    // after. Seeded from whether we opened mid-generation, NOT from whether a draft exists (ORB-485).
    //
    // `hasDraft` looked equivalent and isn't. An Email Copy created from the catalog is bound to the
    // web's Email Template and has that template's design deep-cloned onto it BEFORE the Webster job
    // is queued, so it arrives here brand-new with a draft already present. Seeding off `hasDraft`
    // therefore said "not awaiting" from the start, `rebuilt` took the follow-up branch, and for Email
    // Copy that branch did nothing at all — so nothing ever cleared rocketPhase and the splash idled
    // forever over a generation that had actually succeeded. Reopening the asset showed the finished
    // copy, which is exactly what testers reported.
    //
    // The honest signal is the one the shell already runs on: if we opened generating, we ARE awaiting
    // the initial design, cloned draft or not. Once it lands the flag flips and every later rebuild is
    // a follow-up edit that reseeds in place rather than replaying the splash — too much now that edits
    // are fast (ORB-314).
    const awaitingInitialDesignRef = useRef(shouldStartGenerating)

    // Ends the generating shell — stop the rocket, unlock the input, render the editor. `rebuilt` is a
    // terminal event and so is a failure, so EVERY path out of generation routes through this one
    // helper. Centralizing it is what stops a future edit from clearing one piece of state and
    // forgetting the other — the class of bug ORB-485 was, where the rocket's onComplete was the only
    // exit and a skipped rocket stranded the shell.
    const finishGenerating = useCallback(() => {
        setRocketPhase('ready')
        setAutoGenerating(false)
    }, [])


    // 'rebuilt' SignalR event no longer carries HTML — refetch the asset and pull the
    // canonical DraftDesign / DraftHtml from the response, then reseed the editor with the
    // fresh content. Reset our auto-save tracking refs for Email Template so the user's
    // first post-rebuild edit registers as dirty. Email Copy's panel manages its own design
    // save tracking internally and reseeds off the new asset prop, so no parent-side reset.
    //
    // The rocket plays ONLY for the initial design (awaitingInitialDesignRef); follow-up edits
    // reseed in place — template canvas key-bump; Email Copy self-reseeds off the prop.
    const handleDraftRebuilt = useCallback(async () => {
        const fresh = (await refreshAsset()) as WebAssetViewDto | null
        if (assetType === 'Email Template' && fresh) {
            lastPersistedDesignRef.current = fresh.DraftDesign ?? null
            lastPersistedHtmlRef.current = fresh.DraftHtml ?? null
            pendingDesignRef.current = null
            pendingHtmlRef.current = null
            setTemplateSaveStatus('idle')
            if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
        }
        if (awaitingInitialDesignRef.current) {
            // Initial design: mark it landed, then play the full rocket blast-off. finishGenerating
            // runs when the animation completes (RocketLaunch's onComplete).
            awaitingInitialDesignRef.current = false
            setRocketPhase('blasting-off')
            return
        }

        // Follow-up edit: no rocket, so end the generating shell right here instead of leaving it to
        // an animation that won't play. A no-op on the normal edit path, where the shell is already
        // 'ready' — it exists so a skipped rocket can't strand the editor (ORB-485).
        finishGenerating()

        // Remount the template canvas off the fresh design so it reloads in place.
        // (Email Copy reseeds itself off the refreshed asset prop above.)
        if (assetType === 'Email Template') setTemplateDesignerKey(k => k + 1)
    }, [refreshAsset, assetType, finishGenerating])

    // Revert ("Revert to last version") reloads the editor off the restored draft. Unlike
    // a rebuild it's not animated — no rocket — but it shares the canonical-draft reseed:
    // refetch the asset, reset the auto-save tracking refs to the restored content so the
    // next edit registers as dirty, then remount the template canvas. The Email Copy panel
    // reseeds itself off the refreshed asset prop, so no key bump is needed there.
    const reseedAfterRevert = useCallback(async () => {
        const fresh = (await refreshAsset()) as WebAssetViewDto | null
        // Email Copy reseeds itself off the refreshed asset prop; only the template canvas needs
        // an explicit remount + auto-save-ref reset off the restored draft. Early-return otherwise.
        if (assetType !== 'Email Template' || !fresh) return
        lastPersistedDesignRef.current = fresh.DraftDesign ?? null
        lastPersistedHtmlRef.current = fresh.DraftHtml ?? null
        pendingDesignRef.current = null
        pendingHtmlRef.current = null
        setTemplateSaveStatus('idle')
        if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
        setTemplateDesignerKey(k => k + 1)
    }, [refreshAsset, assetType])

    useEffect(() => {
        reseedRef.current = reseedAfterRevert
        return () => { reseedRef.current = null }
    }, [reseedAfterRevert, reseedRef])

    // A rejected edit ends the build with the draft unchanged. A builder opened mid-run is still on the
    // rocket it started with, and nothing else would take it off.
    const handleRebuildRejected = useCallback(() => {
        awaitingInitialDesignRef.current = false
        finishGenerating()
        void refreshAsset()
    }, [finishGenerating, refreshAsset])

    // Local autoGenerating tracks the IN-MEMORY belief that generation is in
    // flight. When the SB worker writes Failed (persistedGenStatus updates on
    // the next assetDetail refresh) OR an error SignalR event fires, we need
    // to flip autoGenerating off so the failed UI surfaces immediately
    // (rocket animation stops, input unlocks, error display renders). Without
    // this effect, autoGenerating would stay true from its initial-mount
    // value and the rocket would keep animating over the failure banner.
    useEffect(() => {
        if (isFailed && autoGenerating) finishGenerating()
    }, [isFailed, autoGenerating, finishGenerating])

    const { userProfile } = useAuth()
    const {
        messages, generating, sendMessage, beginAutoGeneration,
        turnState, turnUserId, turnUserName,
    } = useEmailAssetChat({
        clientCompanyId,
        workspaceId,
        campaignId,
        assetId,
        assetType,
        initialSessionId: draftConversationSessionId,
        onDraftRebuilt: handleDraftRebuilt,
        // Refetch on a SignalR error so DraftGenerationStatus picks up the
        // Failed value the SB worker just wrote — the useEffect above flips
        // autoGenerating off as soon as isFailed turns true, surfacing the
        // failed UI without making the user refresh.
        onGenerationError: refreshAsset,
        onRebuildRejected: handleRebuildRejected,
    })

    const isGenerating = generating || autoGenerating

    // Open the chat working indicator at generation START for the server-initiated initial
    // generation, so the chat's elapsed clock is continuous (not reset when the first SignalR
    // event lands seconds later) and the chat's `generating` reflects the real lifecycle —
    // ending at `rebuilt`, independent of the rocket animation (autoGenerating, which lingers
    // through the blast-off). useLayoutEffect + a once-guard so it anchors before first paint
    // and fires a single time even as shouldStartGenerating recomputes across status refreshes.
    const autoGenStartedRef = useRef(false)
    useLayoutEffect(() => {
        if (shouldStartGenerating && !autoGenStartedRef.current) {
            autoGenStartedRef.current = true
            beginAutoGeneration()
        }
    }, [shouldStartGenerating, beginAutoGeneration])

    // Multi-user turn lock. When another user is mid-turn on the shared chat
    // session, lock this user's input and attribute the wait. Null when idle
    // or when this user holds the turn (input is already disabled by isGenerating).
    // Fallback to a generic label if turnUserName is missing — without it the
    // input wouldn't lock at all (lockedByUserName goes null), defeating the
    // turn-lock UX when the holder's name happens to be empty on the server side.
    const lockedByUserName = (turnState === 'Active' && turnUserId && turnUserId !== userProfile?.Id)
        ? (turnUserName || 'Another user')
        : null

    // Composer image attachments (ORB-413). Reuses the email-images blob endpoint
    // so attached images live in the same public container the Unlayer picker uses.
    const handleUploadImage = useCallback(async (file: File) => {
        const res = await assetApi.uploadEmailImage(clientCompanyId, workspaceId, file)
        if (!res.data) throw new Error(res.error || 'Failed to upload image')
        return { url: res.data.url }
    }, [clientCompanyId, workspaceId])

    // Bubble the full generating state up so the parent overlay can disable Save-as-Version
    // while Webster is working — initial auto-generation (autoGenerating) AND chat-driven
    // rebuilds (generating). Reporting only `generating` here let users save an un-generated
    // draft during the initial generation (ORB-314).
    useEffect(() => {
        onGeneratingChange?.(isGenerating)
    }, [isGenerating, onGeneratingChange])

    const placeholder = assetType === 'Email Template'
        ? 'Describe the email template you want...'
        : 'Describe the email copy you want...'

    const emptyMessage = assetType === 'Email Template'
        ? 'Ask Webster to create or modify your email template. Describe the design, layout, or changes you want.'
        : 'Ask Webster to draft or refine your email copy. Describe the message, tone, or changes you want.'

    return (
        <div className="h-full flex overflow-hidden">
            {/* Editor area — Email Template uses the canvas editor; Email Copy uses a
                three-pane layout (form fields + body editor | preview | chat). */}
            <div className="flex-1 min-w-0 overflow-hidden">
                {showsFailurePanel ? (
                    /* Persisted Failed status from the SB worker's catch block.
                       Surfaces the actual error and a Try Again affordance instead
                       of the rocket-forever bug pre-ORB-294. The retry just sends
                       a fresh chat message that the orchestrator re-enqueues; no
                       new endpoint needed. */
                    <div className="h-full flex items-center justify-center p-8">
                        <EmailGenerationFailure failure={failure} isTemplate={assetType === 'Email Template'} />
                    </div>
                ) : rocketPhase !== 'ready' ? (
                    <RocketLaunch
                        phase={rocketPhase === 'blasting-off' ? 'blasting-off' : 'idle'}
                        onComplete={finishGenerating}
                        blastOffDuration={2000}
                    />
                ) : assetType === 'Email Template' && designIsEmpty ? (
                    <EmptyDesignRecovery
                        clientCompanyId={clientCompanyId}
                        workspaceId={workspaceId}
                        campaignId={campaignId}
                        assetId={assetId}
                        versions={versions}
                        onRestored={reseedAfterRevert}
                    />
                ) : assetType === 'Email Template' ? (
                    <div className="relative h-full">
                        <UnlayerDesigner
                            key={templateDesignerKey}
                            ref={unlayerEditorRef}
                            initialDesign={assetDetail?.DraftDesign ?? undefined}
                            editable={!isGenerating}
                            onUpdate={handleUnlayerUpdate}
                            mergeTags={templateMergeTags}
                            className="h-full"
                            onReadyChange={setTemplateEditorReady}
                            diagnosticsId={assetId}
                        />
                        {/* Floating save-status pill — only shown when there's something to say. */}
                        {templateSaveStatus !== 'idle' && (
                            <div className="absolute top-2 right-2 z-10 px-2 py-1 rounded-md bg-white/90 dark:bg-slate-900/90 border border-slate-200 dark:border-slate-700 shadow-sm pointer-events-auto">
                                <SaveStatusIndicator
                                    status={templateSaveStatus}
                                    error={templateSaveError}
                                    onRetry={handleTemplateSaveRetry}
                                />
                            </div>
                        )}
                    </div>
                ) : assetDetail ? (
                    <div className="h-full flex flex-col">
                        {templateFieldFailure && <EmailGenerationFailure failure={failure} isTemplate={false} variant="banner" />}
                        <div className="flex-1 min-h-0">
                            <EmailCopyEditorPanel
                                ref={emailCopyPanelRef}
                                clientCompanyId={clientCompanyId}
                                workspaceId={workspaceId}
                                campaignId={campaignId}
                                webId={webId}
                                asset={assetDetail}
                                editable={!isGenerating}
                                onSaved={async () => { await refreshAsset() }}
                                onEditorReadyChange={setCopyEditorReady}
                            />
                        </div>
                    </div>
                ) : null}
            </div>

            {/* Right: Webster chat. Unlayer's editor renders its own inspector inside the
                iframe, so we no longer need our portalled side-panel here. */}
            <div className="w-[420px] flex-shrink-0 flex flex-col border-l border-slate-200 dark:border-slate-700">
                <WebsterChatPanel
                    chat={{ messages, generating }}
                    onSend={sendMessage}
                    onUploadImage={handleUploadImage}
                    disabled={isGenerating}
                    lockedByUserName={lockedByUserName}
                    placeholder={placeholder}
                    emptyMessage={
                        <p className="text-sm text-slate-400 dark:text-slate-500 text-center px-4">
                            {emptyMessage}
                        </p>
                    }
                />
            </div>
        </div>
    )
}

/**
 * The failed state of an email draft. The stored failure is a code, never the worker's text, so this
 * can say what went wrong without showing internals. Email retries go through the chat.
 */
function EmailGenerationFailure({ failure, isTemplate, variant = 'panel' }: {
    failure: GenerationFailureDto | string | null
    isTemplate: boolean
    variant?: 'panel' | 'banner'
}) {
    const view = describeGenerationFailure(failure, isTemplate ? 'template' : 'email')
    const next = view.code === 'TemplateMissingBodySlot'
        ? "Open the template from the web's Assets tab and regenerate it, or choose a different template in this email's Template field."
        : view.code === 'EmailTemplateNotBound'
            ? "Choose a template in this email's Template field, then use the chat to ask Webster again."
            : view.kind === 'ours'
                ? null
                : 'Use the chat to describe what you want and Webster will try again.'
    return (
        <GenerationFailurePanel
            view={view}
            variant={variant}
            lead={null}
            footnote={next ? <span>{next}</span> : undefined}
        />
    )
}
